import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { AuthService } from '../../services/auth-service.service';
import { UserManagementService } from '../../services/user-management.service';
import { ErrorToastService } from '../../services/error-toast.service';

const ACCESS_DENIED_MESSAGE = 'Вход доступен только экспедиторам.';

/**
 * Приём имперсонации Identix: сюда ведёт redirect с одноразовым кодом (?code=…). Обмениваем код на
 * токены целевого пользователя через бэкенд и поднимаем сессию через {@link AuthService#impersonateLogin}
 * (там гейт по роли FORWARDER — в приложение экспедирования пускаем только экспедиторов).
 *
 * <p>Прежняя редакция принимала токен ПРЯМО в URL (?token=…) — механизм Keycloak-стиля, несовместимый
 * с Identix, который выдаёт одноразовый код. Заменён на обмен кода, единый с порталом.
 */
@Component({
  selector: 'app-impersonation-login',
  templateUrl: './impersonation-login.component.html',
  styleUrls: ['./impersonation-login.component.scss']
})
export class ImpersonationLoginComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private userManagementService: UserManagementService,
    private errorToastService: ErrorToastService
  ) {}

  ngOnInit(): void {
    const code = this.route.snapshot.queryParamMap.get('code');
    if (!code) {
      this.router.navigate(['/auth']).catch(() => undefined);
      return;
    }

    this.userManagementService
      .exchangeImpersonationCode(code)
      .pipe(take(1))
      .subscribe({
        next: response => {
          // Обмен не дал токенов (success=false / пустой ответ) — это ошибка обмена, а НЕ отказ по
          // роли: уводим на экран входа без сообщения о роли.
          if (!response?.success || !response.data) {
            this.router.navigate(['/auth']).catch(() => undefined);
            return;
          }

          const data = response.data;
          const granted = this.authService.impersonateLogin(
            data.access_token,
            data.refresh_token,
            data.refresh_expires_in
          );

          if (granted) {
            this.router.navigate(['/']).catch(() => undefined);
          } else {
            this.denyAccess();
          }
        },
        error: () => {
          // Обмен кода не удался (код истёк/использован/сетевой сбой) — на экран входа.
          this.router.navigate(['/auth']).catch(() => undefined);
        }
      });
  }

  /**
   * Токены получены, но у цели нет роли экспедитора — сессия не создана. Если в этом браузере уже есть
   * активная сессия, переход на /auth перехватит {@code AppAuthGuard} и уведёт обратно в приложение,
   * а баннер об отказе не покажется — тогда показываем toast (переживает навигацию). Если сессии нет —
   * баннер на экране входа через {@code ?accessDenied}.
   */
  private denyAccess(): void {
    if (this.authService.isLoggedIn()) {
      void this.errorToastService.showError(ACCESS_DENIED_MESSAGE);
      this.router.navigate(['/auth']).catch(() => undefined);
    } else {
      this.router
        .navigate(['/auth'], { queryParams: { accessDenied: true } })
        .catch(() => undefined);
    }
  }
}
